import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { X } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';

interface PendingRow {
  location_id: string;
  client_organization_id: string;
  client_shopify_location_id: number | null;
  status: string;
  claimed_at: string | null;
  locations?: {
    id: string;
    name: string;
    owner_organization_id: string;
    owner_org?: { id: string; name: string } | null;
  } | null;
}

interface CancelTarget {
  locationId: string;
  locationName: string;
  ownerName: string;
}

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * BLU-389 Phase C — client-side pending-claim visibility.
 *
 * Renders pending_approval junctions where the caller is the CLIENT side.
 * Mirrors the SharedLocationsCard pending-row treatment plus the
 * ConnectedVia3PLSection purple ribbon so the visual language is symmetric.
 *
 * Each card shows: 3PL org name (header pill), location name, claimed-at
 * timestamp, and a "Cancel claim" button. Cancel surfaces an AlertDialog
 * confirmation; the backend emits `revoked_by_client` audit so the owner
 * sees the cancellation in their junction-events log.
 */
export default function ClientPendingClaimsCard() {
  const [pendingCancel, setPendingCancel] = useState<CancelTarget | null>(null);
  const utils = trpc.useUtils();

  const pendingQuery = trpc.locations.listMyPendingClaims.useQuery(undefined, {
    staleTime: 15_000,
  });

  const cancelMutation = trpc.locations.cancelMyPendingClaim.useMutation({
    onSuccess: () => {
      toast({
        title: 'Claim cancelled',
        description:
          'The 3PL provider has been notified. You can submit a new claim anytime if they share a code again.',
      });
      void utils.locations.listMyPendingClaims.invalidate();
      void utils.locations.getOrgRoleSurfaces.invalidate();
      // BLU-389 review: also invalidate locations.list so the §3 surface
      // re-evaluates `sharedLocations.length > 0` and renders the empty
      // state immediately when the last pending claim is cancelled.
      void utils.locations.list.invalidate();
      setPendingCancel(null);
    },
    onError: (err) => {
      toast({
        title: 'Could not cancel claim',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  const rows = useMemo(() => (pendingQuery.data ?? []) as PendingRow[], [pendingQuery.data]);

  if (rows.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        {/* Icon dropped per review — the per-row "Awaiting approval" amber
            badge is the canonical pending signal; the header icon was a
            redundant indicator at a different size. */}
        <CardTitle>Pending invitations</CardTitle>
        <CardDescription>
          Claims waiting on approval from the 3PL provider. They&rsquo;ll review and either approve or reject.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-3">
        {rows.map((r) => {
          const ownerName = r.locations?.owner_org?.name?.trim() || '3PL provider';
          const locationName = r.locations?.name ?? 'Warehouse';
          return (
            <div
              key={`pending-claim-${r.location_id}`}
              className="rounded-lg border border-l-4 border-l-threadheads-purple/30 bg-card p-4 motion-safe:animate-fade-in"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                    {ownerName}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{locationName}</span>
                    <Badge className="bg-amber-100 text-amber-800 ring-1 ring-amber-300/80 text-[10px] uppercase tracking-wide">
                      Awaiting approval
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Claimed {formatDate(r.claimed_at)} ·{' '}
                    {r.client_shopify_location_id === null ? (
                      <span>you will select the Shopify location while pairing the app</span>
                    ) : (
                      <>
                        binds to your Shopify location{' '}
                        <span className="font-mono">{r.client_shopify_location_id}</span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Awaiting approval from {ownerName}. They&rsquo;ll review your claim and either approve or reject.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-orange-300 text-orange-700 hover:bg-orange-50 hover:text-orange-700"
                  onClick={() =>
                    setPendingCancel({
                      locationId: r.location_id,
                      locationName,
                      ownerName,
                    })
                  }
                  disabled={cancelMutation.isPending}
                >
                  <X className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                  Cancel claim
                </Button>
              </div>
            </div>
          );
        })}
      </CardContent>

      <AlertDialog
        open={pendingCancel !== null}
        onOpenChange={(next) => !next && setPendingCancel(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Cancel your pending claim to {pendingCancel?.ownerName ?? 'this 3PL provider'}&rsquo;s {pendingCancel?.locationName ?? 'warehouse'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingCancel?.ownerName ?? 'The provider'} won&rsquo;t see this request anymore. You can re-submit a new claim later if they share a new code.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep claim</AlertDialogCancel>
            <AlertDialogAction
              className="border-orange-300 text-orange-700 hover:bg-orange-50 hover:text-orange-700 bg-transparent"
              disabled={cancelMutation.isPending}
              onClick={() => {
                if (!pendingCancel) return;
                cancelMutation.mutate({ locationId: pendingCancel.locationId });
              }}
            >
              {cancelMutation.isPending ? 'Cancelling…' : 'Cancel claim'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
